import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Globe, Mail, MapPin, MessageCircle, Navigation, Phone, QrCode } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { Button } from "@/components/ui/button";
import { trackInteraction } from "@/storefront/trackInteraction";
import { isWhiteLabelBusiness, advertisedBusinessPath, publicBusinessAddress } from "@/lib/whiteLabel";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareButton } from "@/components/ShareButton";

/** Vertical-flavoured call-button copy the handoff uses ("Call to order" for a restaurant, "Call
 * gym" for a gym) instead of one generic "Call" everywhere — `design_handoff_business_websites/
 * Restaurant Website.dc.html` / `Gym Website.dc.html` ASIDE. Falls back to the generic label for
 * every other vertical (the handoff itself only special-cases these two). */
function callLabel(business, t) {
  const key = `storefront.callLabel.${business.business_type}`;
  const translated = t(key);
  return translated === key ? t("common.call") : translated;
}

/** Address/hours/domain facts + Favourite/Share + Call/WhatsApp/Directions/Email/Pay — moved
 * verbatim from `BusinessMiniSite`'s aside (it was two adjacent boxes there; the design floor
 * names both "ContactRail", so they're one component here). `trackInteraction` fires on
 * call/whatsapp/directions only — email has no `INTERACTION_TYPES` member and would 422, and pay
 * navigates to its own page rather than tracking here; both omissions are load-bearing, not gaps.
 *
 * Storefront S5 (gap review item 3 + 5): Favourite/Share now live here (icon row, top-right of
 * the facts card) — the handoff's hero chip row has no room for them and dropping the feature
 * would be a regression, not a redesign. The "Web address" row is white-label-only now: the
 * handoff's free-tier aside never shows the platform subdomain (every `*.dc.html` ASIDE), but a
 * white-label business's own domain is real identity worth keeping visible.
 */
export function ContactRail({ business }) {
  const { t, lang, tenant } = useApp();
  const navigate = useNavigate();
  const whiteLabel = isWhiteLabelBusiness(business);
  const advertisedPath = advertisedBusinessPath(business);

  return (
    <>
      <div className="space-y-4 rounded-2xl border border-border p-6">
        <div className="flex items-center justify-end gap-2" data-testid="storefront-contact-utility-row">
          <FavoriteButton targetType="business" targetId={business.id} />
          <ShareButton path={advertisedPath} title={localized(lang, business, "name")} testId="business-share-btn" />
        </div>
        {business.address && (
          <p className="flex gap-3 text-sm">
            <MapPin size={16} className="mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
            <span>
              <span className="block text-xs uppercase tracking-widest text-muted-foreground">
                {t("common.address")}
              </span>
              {localized(lang, business, "address")}
            </span>
          </p>
        )}
        {business.hours && (
          <p className="flex gap-3 text-sm">
            <Clock size={16} className="mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
            <span>
              <span className="block text-xs uppercase tracking-widest text-muted-foreground">
                {t("common.hours")}
              </span>
              {localized(lang, business, "hours")}
            </span>
          </p>
        )}
        {/* Storefront S5 item 5: the platform subdomain row is white-label-only now — the
            handoff's free-tier aside never shows it (every `*.dc.html` ASIDE has no "Web
            address" row). A white-label business's own domain is real identity, kept as before. */}
        {whiteLabel && (
          <p className="flex gap-3 text-sm">
            <Globe size={16} className="mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
            {/* `min-w-0` lets this flex item shrink below its content's intrinsic width so
                `break-words` can engage — a long, space-free slug (PR7's home-services presets can
                produce one, e.g. "sri-lakshmi-appliance-repair-services") has no natural break
                point and would otherwise force the aside wider than the viewport at 390px. */}
            <span className="min-w-0">
              <span className="block text-xs uppercase tracking-widest text-muted-foreground">
                {t("business.subdomain")}
              </span>
              {/* R5-11: body text, not mono, and it wraps at the slashes (a <wbr> after each) rather
                  than mid-word; `break-words` only engages for a single segment too long to fit. */}
              <span className="break-words" data-testid="business-web-address">
                {publicBusinessAddress(business, tenant)
                  .split("/")
                  .map((part, i, all) => (
                    <React.Fragment key={i}>
                      {part}
                      {i < all.length - 1 && (
                        <>
                          /<wbr />
                        </>
                      )}
                    </React.Fragment>
                  ))}
              </span>
            </span>
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {business.phone && (
          <Button
            asChild
            className="rounded-full"
            data-testid="business-call-btn"
            onClick={() => trackInteraction(business.id, "call")}
          >
            <a href={`tel:${business.phone}`}>
              <Phone size={15} className="mr-2" /> {callLabel(business, t)}
            </a>
          </Button>
        )}
        {business.whatsapp_number && (
          <Button
            asChild
            variant="outline"
            className="rounded-full"
            data-testid="business-whatsapp-btn"
            onClick={() => trackInteraction(business.id, "whatsapp")}
          >
            <a href={`https://wa.me/${business.whatsapp_number.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
              <MessageCircle size={15} className="mr-2" /> {t("common.whatsapp")}
            </a>
          </Button>
        )}
        {business.email && (
          <Button asChild variant="outline" className="rounded-full" data-testid="business-email-btn">
            {/* No interaction tracking: "email" is not in INTERACTION_TYPES (call/whatsapp/
                directions) and would 422; the mailto link is the whole feature. */}
            <a href={`mailto:${business.email}`}>
              <Mail size={15} className="mr-2" /> {t("common.email")}
            </a>
          </Button>
        )}
        {business.address && (
          <Button
            asChild
            variant="outline"
            className="rounded-full"
            data-testid="business-directions-btn"
            onClick={() => trackInteraction(business.id, "directions")}
          >
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.address)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Navigation size={15} className="mr-2" /> {t("common.directions")}
            </a>
          </Button>
        )}
        {business.accepts_upi && (
          <Button
            variant="outline"
            className="rounded-full"
            data-testid="business-pay-btn"
            onClick={() => navigate(`/pay/${business.id}`)}
          >
            <QrCode size={15} className="mr-2" /> {t("payment.payThisShop")}
          </Button>
        )}
      </div>
    </>
  );
}
